import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { notificationService } from '../services/notification.service';
import { teamService } from '../services/team.service';
import { toast } from 'sonner';


export type NotificationType = 'INVITE' | 'TEAM' | 'OTHER';

export interface Notification {
    _id: string;
    recipient: string;
    sender?: string;
    type: NotificationType;
    message: string;
    teamId?: string;
    referenceId?: string;
    read: boolean;
    createdAt: string;
}

interface NotificationState {
    notifications: Notification[];
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    acceptInvite: (notification: Notification) => Promise<void>;

    // Derived
    unreadCount: () => number;
}

export const useNotificationStore = create<NotificationState>()(
    devtools(
        persist(
            (set, get) => ({
                notifications: [],
                isLoading: false,
                error: null,

                fetchNotifications: async () => {
                    set({ isLoading: true, error: null });
                    try {
                        const notifications = await notificationService.getUserNotifications();
                        set({ notifications, isLoading: false, error: null });
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to fetch notifications',
                        });
                    }
                },

                markAsRead: async (id) => {
                    await notificationService.markAsRead(id);
                    set((state) => ({
                        notifications: state.notifications.map((n) =>
                            n._id === id ? { ...n, read: true } : n
                        ),
                    }));

                },
                acceptInvite: async (notification: Notification) => {
                    const referenceId = notification.referenceId;
                    const notificationId =  notification._id;

                    if (!referenceId || !notificationId) {
                        toast.error('Invalid invitation');
                        return;
                    }
                    await teamService.acceptInvitation(referenceId);

                },

                unreadCount: () => get().notifications.filter(n => !n.read).length,
            }),
            {
                name: 'notification-storage',
                partialize: (state) => ({
                    notifications: state.notifications,
                }),
            }
        ),
        { name: 'notification-store' }
    )
);
